<template>
  <ion-modal :is-open="true" @onDidDismiss="$emit('dismiss')">
    <ion-page>
      <MyHeader
        :show-close="true"
        :title="date.format('YYYY/MM/DD')"
        @close="$emit('dismiss')"
      >
        <div class="totalPrice">
          ￥{{
            editingPageData ? editingPageData.totalPrice.toLocaleString() : 0
          }}
        </div>
      </MyHeader>
      <ion-content class="ion-no-padding">
        <ion-list>
          <template
            v-for="(purchase, index) in editingPurchases"
            :key="purchase.id"
          >
            <PurchaseListItem
              v-show="!purchase.deleteFlg"
              :purchase="purchase"
              @update="updateEditingPurchase($event, Number(index))"
            />
          </template>
        </ion-list>
        <ion-button
          class="button ion-no-padding ion-no-margin"
          color="dark"
          fill="clear"
          expand="full"
          :disabled="!canAdd"
          @click="addItem(date.format('YYYY-MM-DD'))"
        >
          <ion-icon :icon="add" />
          <span>追加</span>
        </ion-button>
      </ion-content>
      <ion-footer>
        <ion-button
          class="button"
          color="primary"
          shape="round"
          :disabled="!canSave"
          @click="saveCalendarPage()"
        >
          <ion-label>保存する</ion-label>
        </ion-button>
      </ion-footer>
    </ion-page>
  </ion-modal>
</template>

<script lang="ts">
import {
  IonIcon,
  IonList,
  IonLabel,
  IonFooter,
  IonContent,
  IonButton,
  IonModal,
  IonPage,
} from '@ionic/vue'
import { defineComponent, PropType, computed } from 'vue'
import { add } from 'ionicons/icons'
import { Dayjs } from 'dayjs'
import PurchaseListItem from '@/components/purchase/PurchaseListItem.vue'
import { useCalendarPageModule } from '@/provider/CalendarPageModule'
import { openToast } from '@/composable/toast'
import MyHeader from '@/components/layouts/MyHeader.vue'
import {
  PurchaseInput,
  UpdateCalendarPageInput,
  useCalendarPageQuery,
  useUpdateCalendarPageMutation,
} from '@/composable/apollo/graphql'
export default defineComponent({
  components: {
    IonIcon,
    IonList,
    IonLabel,
    IonFooter,
    IonContent,
    IonButton,
    IonModal,
    IonPage,
    PurchaseListItem,
    MyHeader,
  },
  props: {
    date: {
      type: Object as PropType<Dayjs>,
      required: true,
    },
  },
  emits: ['dismiss'],
  setup(props, { emit }) {
    const {
      editingPurchases,
      updateEditingPurchase,
      editingPageData,
      addItem,
      calendarPageTemplateId,
      setEditingCalendarPage,
      resetEditingPurchases,
    } = useCalendarPageModule()
    const { result, onResult, loading } = useCalendarPageQuery({
      date: props.date.format('YYYY-MM-DD'),
    })
    if (result.value) {
      setEditingCalendarPage(result.value.calendarPage ?? undefined)
    } else {
      resetEditingPurchases()
      onResult((result) =>
        setEditingCalendarPage(result.data.calendarPage ?? undefined)
      )
    }
    const calendarPageInput = computed<UpdateCalendarPageInput>(() => {
      const purchases = purchasesPostParams.value
      return {
        date: props.date.format('YYYY-MM-DD'),
        purchases,
      }
    })
    const {
      mutate,
      onDone: onDoneSaveCalendarPage,
    } = useUpdateCalendarPageMutation({})
    onDoneSaveCalendarPage(() => {
      openToast('データが保存されました')
      emit('dismiss')
    })
    const purchasesPostParams = computed<PurchaseInput[]>(() =>
      Object.values(editingPurchases)
        .filter((purchase) => {
          return !purchase.deleteFlg
        })
        .map((purchase, i) => ({
          category: purchase.category,
          deleteFlg: purchase.deleteFlg,
          memo: purchase.memo || '',
          price: purchase.price,
          position: i,
        }))
    )
    const canAdd = computed(() => {
      if (loading.value) return false
      return Object.keys(editingPurchases).length < 20
    })
    const canSave = computed(() => {
      if (loading.value) return false
      const purchases = purchasesPostParams.value
      return (
        purchases.length > 0 &&
        purchases.every(
          (purchase) => !!purchase.category && purchase.price >= 0
        )
      )
    })
    const saveCalendarPage = () => {
      const input = calendarPageInput.value
      if (calendarPageTemplateId.value) {
        // テンプレートの場合はIDを削除
        input.purchases = input.purchases.reduce((acc, pur) => {
          return pur.deleteFlg ? acc : [...acc, pur]
        }, [] as PurchaseInput[])
      }
      mutate({
        calendarPage: input as UpdateCalendarPageInput,
      })
    }

    return {
      add,
      close,
      addItem,
      editingPurchases,
      updateEditingPurchase,
      editingPageData,
      canAdd,
      canSave,
      saveCalendarPage,
    }
  },
})
</script>

<style lang="scss" scoped>
.button {
  width: 100%;
  margin: 0;
}
.item-button {
  display: flex;
  justify-content: center;
  align-items: center;
}
.totalPrice {
  display: inline-block;
}
</style>
